/**
 * RankingList — the numbered, scored list of sites, as props (MYD-502).
 *
 * Pure: no store, no fetching. RankingCard feeds it on the Scoring tab and a
 * presentation slide feeds it from a resolved payload, so the two cannot drift.
 * `size="slide"` scales the type for the 1280x720 stage; the default is the
 * Scoring tab's, unchanged.
 */

import { cn } from 'lib/utils'

export interface RankingEntry {
	id: string
	name: string
	/** Shown to one decimal; null entries are the caller's to leave out. */
	score: number | null
	/** A marker after the name, e.g. the evaluation's selected site. */
	badge?: string
}

// One row is a 24px rank badge plus py-1.5 top and bottom (12px); space-y-1.5
// puts 6px between rows. Tailwind can't express a height derived from a prop at
// runtime, so this is applied as a computed maxHeight (same reason
// ComparisonScoring sets gridTemplateColumns inline).
const ROW_H = 36
const ROW_GAP = 6
const listMaxHeight = (rows: number) => rows * ROW_H + (rows - 1) * ROW_GAP

interface RankingListProps {
	entries: RankingEntry[]
	/** Show this many rows and scroll the rest (card size only). */
	maxVisible?: number
	size?: 'card' | 'slide'
}

const RankingList = ({ entries, maxVisible, size = 'card' }: RankingListProps) => {
	const slide = size === 'slide'
	const scrolls = !slide && maxVisible != null && entries.length > maxVisible

	return (
		<ol
			className={cn(
				slide ? 'space-y-3' : 'space-y-1.5',
				// pr-1 keeps the scrollbar off the score column.
				scrolls && 'overflow-y-auto pr-1'
			)}
			style={scrolls ? { maxHeight: listMaxHeight(maxVisible as number) } : undefined}
		>
			{entries.map((entry, idx) => (
				<li
					key={entry.id}
					className={cn(
						'flex items-center rounded-md',
						slide ? 'gap-4 px-4 py-3' : 'gap-2 px-2 py-1.5',
						idx === 0 && 'bg-[#0080fe]/5 border border-[#0080fe]/20'
					)}
				>
					<div
						className={cn(
							'rounded-full flex items-center justify-center font-semibold shrink-0',
							slide ? 'w-11 h-11 text-[20px]' : 'w-6 h-6 text-xs',
							idx === 0 ? 'bg-[#0080fe] text-white' : 'bg-gray-100 text-gray-600'
						)}
					>
						{idx + 1}
					</div>
					<div className="flex-1 min-w-0">
						<div className={cn('font-medium text-gray-900 truncate', slide ? 'text-[24px]' : 'text-sm')}>
							{entry.name}
							{entry.badge && (
								<span className={cn('ml-2 rounded-full bg-emerald-50 font-medium text-emerald-700', slide ? 'px-2.5 py-0.5 text-[15px]' : 'px-1.5 text-[10px]')}>
									{entry.badge}
								</span>
							)}
						</div>
					</div>
					<div className={cn('font-semibold text-gray-900 shrink-0', slide ? 'text-[26px] tabular-nums' : 'text-sm')}>{entry.score?.toFixed(1)}</div>
				</li>
			))}
		</ol>
	)
}

export default RankingList
